import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import {
	Button,
	Form,
	Input,
	Modal,
	Popconfirm,
	Space,
	Table,
	Typography,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";

import getUserFromToken from "../../utils/getUserFromToken";
import {
	deleteExpenseType,
	loadExpenseTypes,
	saveExpenseType,
} from "../../redux/rtk/features/expense/expenseTypeSlice";

const ExpenseTypeSetup = () => {
	const dispatch = useDispatch();
	const [form] = Form.useForm();

	const { list = [], loading } = useSelector((state) => state.expenseType || {});

	const userInfo = getUserFromToken();
	const companyId = userInfo?.CompanyId ?? userInfo?.companyId ?? null;

	const [editingRecord, setEditingRecord] = useState(null);
	const [formModalOpen, setFormModalOpen] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [deletingId, setDeletingId] = useState(null);

	useEffect(() => {
		dispatch(loadExpenseTypes());
	}, [dispatch]);

	const resetForm = useCallback(() => {
		form.resetFields();
		setEditingRecord(null);
	}, [form]);

	const openAddModal = useCallback(() => {
		resetForm();
		setFormModalOpen(true);
	}, [resetForm]);

	const closeFormModal = useCallback(() => {
		setFormModalOpen(false);
		resetForm();
	}, [resetForm]);

	const handleEdit = useCallback(
		(record) => {
			if (!record) {
				return;
			}
			form.setFieldsValue({
				ExpanseTypeName: record?.ExpanseTypeName ?? "",
				ExpanseTypeDescription: record?.ExpanseTypeDescription ?? "",
			});
			setEditingRecord({
				ExpanseTypeId: Number(record?.ExpanseTypeId) || 0,
				DataStatus: Number(record?.DataStatus) || 1,
			});
			setFormModalOpen(true);
		},
		[form]
	);

	const handleSubmit = useCallback(async () => {
		try {
			const values = await form.validateFields();
			setSubmitting(true);
			const payload = {
				ExpanseTypeId: editingRecord?.ExpanseTypeId ?? 0,
				ExpanseTypeName: values.ExpanseTypeName?.trim(),
				ExpanseTypeDescription: values.ExpanseTypeDescription?.trim() || null,
				DataStatus: editingRecord?.DataStatus ?? 1,
				CompanyId: companyId,
			};
			const response = await dispatch(saveExpenseType(payload));
			if (response?.meta?.requestStatus !== "rejected") {
				closeFormModal();
				dispatch(loadExpenseTypes());
			}
		} finally {
			setSubmitting(false);
		}
	}, [companyId, closeFormModal, dispatch, editingRecord, form]);

	const handleDelete = useCallback(
		async (record) => {
			const expanseTypeId = Number(record?.ExpanseTypeId);
			if (!expanseTypeId) {
				return;
			}
			setDeletingId(expanseTypeId);
			try {
				const response = await dispatch(
					deleteExpenseType({
						ExpanseTypeId: expanseTypeId,
					})
				);
				if (response?.meta?.requestStatus !== "rejected") {
					if (editingRecord?.ExpanseTypeId === expanseTypeId) {
						closeFormModal();
					}
					dispatch(loadExpenseTypes());
				}
			} finally {
				setDeletingId(null);
			}
		},
		[closeFormModal, dispatch, editingRecord]
	);

	const columns = useMemo(
		() => [
			{
				title: "Expense Type Name",
				dataIndex: "ExpanseTypeName",
				key: "ExpanseTypeName",
			},
			{
				title: "Description",
				dataIndex: "ExpanseTypeDescription",
				key: "ExpanseTypeDescription",
				render: (value) => value || "-",
			},
			{
				title: "Status",
				dataIndex: "DataStatus",
				key: "DataStatus",
				render: (value) => {
					const numeric = Number(value);
					if (numeric === 1) return "Active";
					if (numeric === 0) return "Inactive";
					if (numeric === 2) return "Deleted";
					return value ?? "-";
				},
			},
			{
				title: "Actions",
				key: "actions",
				render: (_, record) => (
					<Space>
						<Button
							type="link"
							icon={<EditOutlined />}
							onClick={() => handleEdit(record)}
						/>
						<Popconfirm
							title="Delete expense type"
							description={`Are you sure you want to delete ${record.ExpanseTypeName}?`}
							okText="Yes"
							cancelText="No"
							onConfirm={() => handleDelete(record)}
						>
							<Button
								type="link"
								danger
								icon={<DeleteOutlined />}
								loading={deletingId === record.ExpanseTypeId}
							/>
						</Popconfirm>
					</Space>
				),
			},
		],
		[deletingId, handleDelete, handleEdit]
	);

	const isLogged = Boolean(localStorage.getItem("isLogged"));
	if (!isLogged) {
		return <Navigate to="/admin/auth/login" replace />;
	}

	return (
		<div className="add-expense-type-page-wrapper">
			<div className="ant-card p-4 rounded expense-type-table-container">
				<div className="flex my-2 justify-between items-center">
					<Typography.Title level={4} style={{ margin: 0 }}>
						Expense Types
					</Typography.Title>
					<Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>
						Expense
					</Button>
				</div>

				{loading ? (
					<div className="text-center py-8">
						<div className="ant-spin ant-spin-lg ant-spin-spinning">
							<span className="ant-spin-dot ant-spin-dot-spin">
								<i className="ant-spin-dot-item"></i>
								<i className="ant-spin-dot-item"></i>
								<i className="ant-spin-dot-item"></i>
								<i className="ant-spin-dot-item"></i>
							</span>
						</div>
						<p className="mt-4 text-gray-600">Loading expense types...</p>
					</div>
				) : !list || list.length === 0 ? (
					<div className="text-center py-8">
						<p className="text-gray-600">No expense types found.</p>
					</div>
				) : (
					<Table
						rowKey={(record) => record?.ExpanseTypeId ?? record?.key}
						dataSource={list}
						columns={columns}
						loading={loading}
						scroll={{ x: true, y: "100%" }}
						pagination={{
							defaultPageSize: 20,
						}}
						className="expense-type-table"
					/>
				)}
			</div>

			<Modal
				title={editingRecord ? "Update Expense Type" : "Add Expense Type"}
				open={formModalOpen}
				onCancel={closeFormModal}
				footer={null}
				destroyOnClose
			>
				<Form
					form={form}
					layout="vertical"
					onFinish={handleSubmit}
					autoComplete="off"
				>
					<Form.Item
						label="Expense Type Name"
						name="ExpanseTypeName"
						rules={[
							{ required: true, message: "Please enter expense type name" },
						]}
					>
						<Input placeholder="Enter expense type name" />
					</Form.Item>

					<Form.Item label="Description" name="ExpanseTypeDescription">
						<Input.TextArea rows={3} placeholder="Enter description (optional)" />
					</Form.Item>

					<Form.Item className="mb-0">
						<Space>
							<Button type="primary" htmlType="submit" loading={submitting}>
								{editingRecord ? "Update" : "Add"}
							</Button>
							<Button onClick={closeFormModal} disabled={submitting}>
								Cancel
							</Button>
						</Space>
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
};

export default ExpenseTypeSetup;
